import {useMemo, useState, useCallback} from "react";
import {useCompanyId} from "@/hooks/use-company-id";
import {useTemplates} from "@/hooks/queries/use-templates";
import {useUserRoles} from "@/hooks/use-user-roles";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {useRouter} from "next/navigation";
import {projectPath} from "@/app/paths";
import {DocumentInitialInput} from "@/pb/api/devplan/types/documents";
import type {IssueAttachmentMetadata} from "@/pb/api/devplan/types/attachments";
import {ProjectTemplate, TemplateStatus} from "@/pb/api/devplan/types/templates";
import {filterTemplates, orderTemplates} from "@/app/components/projects/templatesFilter";
import {toast} from "sonner";
import {event as trackEvent} from "@/lib/analytics";

// Shared hook for project template logic
export function useProjectTemplates() {
  const {templates, error} = useTemplates()
  const {admin} = useUserRoles()
  const projectTemplates = useMemo(
    () => orderTemplates(filterTemplates(templates?.projectTemplates || [], ["project"])
      .filter(t => t.meta?.status === TemplateStatus.ACTIVE || (admin && t.meta?.status === TemplateStatus.DRAFT))),
    [admin, templates?.projectTemplates])
  
  return { projectTemplates, templates, error }
}

export function getDefaultProjectTemplateId(projectTemplates: ProjectTemplate[]): string | undefined {
  if (projectTemplates.length === 0) {
    return undefined
  }
  // Templates are ordered by quickLaunchOrder ascending, so the last one has the highest order.
  return projectTemplates[projectTemplates.length - 1].id
}

export interface ProjectCreateParams {
  description: string;
  imageUrls?: string[];
  attachmentIds?: string[];
  issueAttachments?: IssueAttachmentMetadata[];
  uploadedSpec?: boolean;
  kind?: string;
  /** Allow creating a project with an empty description (e.g. title-only inline creation). */
  allowEmptyDescription?: boolean;
  /**
   * User-supplied project title. When non-empty, the server uses it directly and skips
   * AI-driven title generation (see `runProjectTitleGeneration` short-circuit).
   */
  title?: string;
  /** Optional initial owner user id — applied atomically at project creation. */
  ownerId?: string;
  /** Optional initial release id — applied atomically at project creation. */
  releaseId?: string;
  /** Optional initial target/delivery date — applied atomically at project creation. */
  targetDate?: Date;
  /**
   * Optional section key in the workspace projects group. When set, the server
   * inserts the new project into that section's `childItems` inside the same
   * transaction as project creation — no follow-up group update needed.
   */
  sectionKey?: string;
  onSuccess?: (projectId: string) => void | Promise<void>
  onError?: () => void
}

export interface ProjectCreationParams {
  templates: ProjectTemplate[]
  templateId: string | undefined
  redirectPath?: (projectId: string) => string
}

// Shared hook for project creation logic
export function useProjectCreation({templateId, templates, redirectPath}: ProjectCreationParams) {
  const {companyId} = useCompanyId()
  const {createProjectWithRequirements} = useBoundStore()
  const router = useRouter()
  const [creating, setCreating] = useState(false)

  const createProject = useCallback(async (params: ProjectCreateParams) => {
    const {description, imageUrls, attachmentIds, issueAttachments, uploadedSpec, kind, allowEmptyDescription, title, ownerId, releaseId, targetDate, sectionKey, onSuccess, onError} = params;
    if ((!description || description.trim().length === 0) && !allowEmptyDescription) {
      toast.error(uploadedSpec ? "Please paste or upload a spec" : "Please enter a description");
      return
    }
    setCreating(true)
    try {
      const template = templates.find(t => t.id === templateId)
      const reqTemplateId = template?.details?.requirements?.info?.templateId;
      if (!template || !reqTemplateId) {
        throw new Error("requirements template id is not properly defined");
      }
      const input = DocumentInitialInput.create(
        {structuredInput: [{name: "description", value: description}], imageUrls, attachmentIds, issueAttachments},
      )
      const trimmedTitle = title?.trim()
      const projectId = await createProjectWithRequirements(companyId, {
        projectTemplateId: template.id,
        initialInput: input,
        uploadedSpec: uploadedSpec || false,
        descriptionAsPrdContent: false,
        description,
        kind,
        tagIds: [],
        ...(trimmedTitle ? {title: trimmedTitle} : {}),
        ...(ownerId ? {owner: ownerId} : {}),
        ...(releaseId !== undefined ? {releaseId} : {}),
        ...(targetDate ? {targetDate} : {}),
        ...(sectionKey ? {sectionKey} : {}),
      })
      if (onSuccess) await onSuccess(projectId)
      const routeTo = redirectPath?.(projectId) ?? projectPath(companyId, projectId)
      if (routeTo.length > 0) {
        router.push(routeTo)
      }
    } catch (e: unknown) {
      toast.error(String(e instanceof Error ? e.message : e))
      if (uploadedSpec) {
        trackEvent({eventName: 'create_project_from_spec_error', category: 'project'})
      }
      if (onError) onError()
    } finally {
      setCreating(false)
    }
  }, [companyId, createProjectWithRequirements, redirectPath, router, templateId, templates])

  return { createProject, creating }
}
